import { FaVolleyballBall, FaRunning } from "react-icons/fa";
import { GiTennisCourt, GiShuttlecock } from "react-icons/gi";

// Hero Image
import heroImage from "../assets/services/football/hero.webp";
import overview1 from "../assets/services/football/overview1.webp";
import overview2 from "../assets/services/football/overview2.webp";
import overview3 from "../assets/services/football/overview3.webp";
import overview4 from "../assets/services/football/overview4.webp";

/* =====================================================
   HERO STATS
===================================================== */

// Gallery Images
import gallery1 from "../assets/services/football/gallery1.webp";
import gallery2 from "../assets/services/football/gallery2.webp";
import gallery3 from "../assets/services/football/gallery3.webp";
import gallery4 from "../assets/services/football/gallery4.webp";

const footballData = {
  /* =====================================================
     HERO
  ====================================================== */

  slug: "football-field",

  title: "Football Field Construction",

  subtitle:
    "Professional Football Fields Built for High Performance, Safety & Durability",

  heroImage,

  overviewImages: [overview1, overview2, overview3, overview4],

  heroStats: [
    {
      value: "250+",
      label: "Fields Built",
    },

    {
      value: "15+",
      label: "Years Experience",
    },

    {
      value: "FIFA",
      label: "Standard",
    },

    {
      value: "2 Yr",
      label: "Warranty",
    },
  ],

  breadcrumb: ["Home", "Services", "Football Field"],

  /* =====================================================
     OVERVIEW
  ====================================================== */

  overview: {
    title: "Professional Football Field Construction",

    description:
      "Quality Sports Surface specializes in designing and constructing professional football fields for schools, colleges, universities, sports academies, clubs, residential communities, corporate facilities, and professional sports organizations. We provide complete football field solutions using high-quality natural grass and synthetic turf systems engineered for excellent playing performance, player safety, durability, drainage, weather resistance, and long-term use.",
  },

  /* =====================================================
     BENEFITS
  ====================================================== */

  benefits: [
    {
      title: "Professional Standard Construction",
      description:
        "Football fields are designed according to professional playing dimensions and international standards.",
    },

    {
      title: "Premium Synthetic Turf",
      description:
        "High-performance artificial grass systems provide excellent ball roll, traction, durability, and consistent playing conditions.",
    },

    {
      title: "Superior Drainage System",
      description:
        "Professionally engineered drainage systems help prevent water accumulation and maintain field usability during heavy rainfall.",
    },

    {
      title: "Player Safety",
      description:
        "Shock-absorbing and carefully engineered playing surfaces help reduce impact and provide comfortable movement for players.",
    },
  ],

  /* =====================================================
     CONSTRUCTION PROCESS
  ====================================================== */

  process: [
    {
      step: "01",
      title: "Site Inspection",
      description:
        "Complete site survey, measurements, soil assessment, slope analysis, and technical planning.",
    },

    {
      step: "02",
      title: "Ground Preparation",
      description:
        "Site clearing, excavation, leveling, compaction, and preparation of the field foundation.",
    },

    {
      step: "03",
      title: "Drainage Installation",
      description:
        "Professional drainage system installation to efficiently remove rainwater and prevent waterlogging.",
    },

    {
      step: "04",
      title: "Base Preparation",
      description:
        "Installation and compaction of engineered sub-base layers to create a stable and level playing surface.",
    },

    {
      step: "05",
      title: "Turf Installation",
      description:
        "Professional installation of premium synthetic turf according to project requirements.",
    },

    {
      step: "06",
      title: "Infill & Finishing",
      description:
        "Application of suitable infill materials, brushing, leveling, and final surface preparation.",
    },

    {
      step: "07",
      title: "Field Marking",
      description:
        "Accurate football field line marking including sidelines, penalty areas, center circle, goal areas, and other required markings.",
    },

    {
      step: "08",
      title: "Final Inspection",
      description:
        "Complete quality inspection and performance checks before final project handover.",
    },
  ],

  /* =====================================================
     TECHNICAL SPECIFICATIONS
  ====================================================== */

  specifications: [
    {
      icon: "⚽",
      feature: "Surface Type",
      value: "Premium Synthetic Football Turf",
    },

    {
      icon: "🌱",
      feature: "Turf Options",
      value: "Synthetic Grass",
    },

    {
      icon: "🌦",
      feature: "Weather Resistance",
      value: "Excellent",
    },

    {
      icon: "💧",
      feature: "Drainage System",
      value: "High-Efficiency Drainage",
    },

    {
      icon: "👟",
      feature: "Traction",
      value: "High Grip & Player Safe",
    },

    {
      icon: "🛡️",
      feature: "Shock Absorption",
      value: "Advanced Impact Protection",
    },

    {
      icon: "⏳",
      feature: "Expected Lifespan",
      value: "8+ Years",
    },

    {
      icon: "🏟️",
      feature: "Field Type",
      value: "Training & Professional Fields",
    },
  ],

  /* =====================================================
     GALLERY SECTION
  ====================================================== */

  gallerySection: {
    tag: "FEATURED PROJECTS",

    title: "Our Recent Football Field Projects",

    description:
      "Explore some of the professional football fields successfully completed by Quality Sports Surface across India.",
  },

  /* =====================================================
     GALLERY
  ====================================================== */

  gallery: [
    {
      image: gallery1,
      title: "Academic Height Public School",
      location: "Hyderabad",
      surface: "Premium Synthetic Football Turf",
      year: "2026",
    },

    {
      image: gallery2,
      title: "Gowtham World School (Nizampet)",
      location: "Hyderabad",
      surface: "Professional Football Turf",
      year: "2026",
    },

    {
      image: gallery3,
      title: "Sportiva Club-USB Square(RC Puram)",
      location: "Hyderabad",
      surface: "Outdoor Football Field",
      year: "2026",
    },

    {
      image: gallery4,
      title: "University Campus",
      location: "Chandigarh",
      surface: "Professional Standard Football Field",
      year: "2023",
    },
  ],

  /* =====================================================
     WHY CHOOSE US — FOOTBALL
  ====================================================== */

  whyChoose: {
    tag: "WHY CHOOSE US",

    title: "Why Choose Our Football Field Solutions?",

    description:
      "We deliver professional football field construction solutions designed for superior player safety, consistent playing performance, excellent drainage, durability, and long-term use. From ground preparation and drainage to turf installation and field marking, we provide complete football infrastructure solutions tailored to your requirements.",

    features: [
      {
        title: "15+ Years Experience",
        description:
          "Over 15 years of experience delivering professional sports infrastructure and football field solutions for schools, academies, clubs, universities, residential communities, and sports facilities across India.",
      },

      {
        title: "500+ Sports Projects",
        description:
          "Successfully completed 500+ sports infrastructure projects, including football fields, synthetic turf grounds, multi-sport facilities, educational institutions, academies, and professional training facilities.",
      },

      {
        title: "Premium Synthetic Football Turf",
        description:
          "We use high-quality sports-grade synthetic turf systems designed for durability, consistent ball roll, excellent traction, player comfort, and reliable performance during regular football activities.",
      },

      {
        title: "Advanced Shock Absorption",
        description:
          "Our football turf systems can incorporate shock-absorbing underlay and infill solutions to provide a comfortable playing surface and help reduce impact on players' knees, ankles, and joints.",
      },

      {
        title: "Excellent Ball Roll & Traction",
        description:
          "Professionally installed football surfaces provide consistent ball movement, controlled traction, and reliable grip to support sprinting, turning, tackling, passing, and changes of direction.",
      },

      {
        title: "Professional Drainage System",
        description:
          "Our field construction solutions include professionally designed drainage systems that help remove rainwater efficiently and reduce waterlogging, allowing the field to remain playable in changing weather conditions.",
      },

      {
        title: "Professional Field Marking",
        description:
          "Football fields are finished with accurate professional markings including sidelines, goal lines, penalty areas, center lines, center circles, and other required field markings.",
      },

      {
        title: "Durable & Long-Lasting",
        description:
          "High-performance turf, quality base materials, proper drainage, and professional installation help the field withstand regular training sessions, matches, tournaments, and continuous sports activity.",
      },

      {
        title: "Custom Football Field Solutions",
        description:
          "We provide customized football field solutions based on available space, field dimensions, usage intensity, player requirements, budget, drainage conditions, and performance expectations.",
      },

      {
        title: "Indoor & Outdoor Solutions",
        description:
          "Our team can design and construct football playing surfaces for outdoor grounds, training facilities, academies, schools, residential communities, and other sports infrastructure projects.",
      },

      {
        title: "On-Time Project Delivery",
        description:
          "Our professional project management process helps ensure efficient execution and completion within committed timelines while minimizing disruption to your sports facility.",
      },

      {
        title: "Complete Project Support",
        description:
          "From initial consultation and site assessment to ground preparation, drainage, turf installation, field marking, maintenance guidance, and after-sales support, we provide complete project assistance.",
      },

      {
        title: "Maintenance & After-Sales Support",
        description:
          "We provide practical maintenance guidance and ongoing support to help preserve the appearance, traction, drainage performance, durability, and playing quality of your football field.",
      },
    ],
  },

  /* =====================================================
     FAQ
  ====================================================== */

  faqs: [
    {
      question: "How long does football field construction take?",
      answer:
        "The construction timeline generally depends on field size, site conditions, drainage requirements, base preparation, and turf selection. Most projects can be completed within 30-60 days.",
    },

    {
      question: "Can synthetic football turf be used outdoors?",
      answer:
        "Yes. Professional synthetic football turf is specifically designed for outdoor sports facilities and can withstand sunlight, rain, and regular playing activity.",
    },

    {
      question: "How long does synthetic football turf last?",
      answer:
        "With proper installation and regular maintenance, high-quality synthetic football turf can typically provide 8+ years of service, depending on usage and environmental conditions.",
    },

    {
      question: "Do you construct FIFA-standard football fields?",
      answer:
        "Yes. We provide professional football field construction solutions designed around applicable FIFA recommendations and project-specific field requirements.",
    },

    {
      question: "Do you provide drainage for football fields?",
      answer:
        "Yes. We design and install suitable drainage systems to efficiently remove rainwater and reduce waterlogging based on the site's soil and weather conditions.",
    },

    {
      question: "Can you construct football fields for schools and academies?",
      answer:
        "Yes. We construct football fields for schools, colleges, universities, sports academies, clubs, residential communities, corporate facilities, and professional training centers.",
    },
  ],

  /* =====================================================
     RELATED SERVICES
  ====================================================== */

  relatedServices: [
    {
      icon: GiTennisCourt,
      title: "Tennis Court",
      description: "Professional ITF Standard Courts",
      link: "/services/tennis-court",
    },

    {
      icon: GiShuttlecock,
      title: "Badminton Court",
      description: "Professional BWF Standard Courts",
      link: "/services/badminton-court",
    },

    {
      icon: FaRunning,
      title: "Running Track",
      description: "Athletic Running Track Systems",
      link: "/services/running-track",
    },

    {
      icon: FaVolleyballBall,
      title: "Volleyball Court",
      description: "Professional FIVB Standard Courts",
      link: "/services/volleyball-court",
    },
  ],

  /* =====================================================
     CTA
  ====================================================== */

  cta: {
    title: "Ready to Build Your Football Field?",

    description:
      "Talk with our experts today and get a free consultation for your next football field or sports infrastructure project.",
  },
};

export default footballData;
